/* eslint-disable react/no-array-index-key */
import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { setMassiveCommunicationsCount } from 'redux/actions/resultActions';

import { L10n, createElement, setCulture } from '@syncfusion/ej2-base';
import { TextBox } from '@syncfusion/ej2-react-inputs';
import { TextField } from 'shared/components/form/Inputs';
import { ReactComponent as SearchIcon } from 'assets/icon-search.svg';
import { colors } from 'shared/defaultTheme';
import {
  GridComponent,
  ColumnsDirective,
  ColumnDirective,
  Page,
  Inject,
  Filter,
  Resize,
} from '@syncfusion/ej2-react-grids';
import useApi from 'hooks/useApi';
import { removeBreadcrumb } from 'redux/actions/layoutActions';
import useNotification from 'hooks/useNotification';
import { capitalizeFirstLetter } from 'shared/utils';
import { DataUtil } from '@syncfusion/ej2-data';
import { setShowTypeNotifications } from 'redux/actions/notificationActions';
import PropTypes from 'prop-types';
import { DocumentationResultsWrapper } from '../styles';
import { L10nObj } from '../contants';

DataUtil.serverTimezoneOffset = 2 * (new Date().getTimezoneOffset() / 60);

const MassiveCommunicationResults = forwardRef(({ getCounters }, ref) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const [filters, setFilters] = useState([]);
  const [sorting, setSorting] = useState();
  const { generalSearchText } = useSelector((state) => state.results);
  const { breadcrumbs } = useSelector((state) => state.layout);
  const { isCustomer, mail, cusNavId, clientId } = useSelector((state) => state.auth);
  const { selectedDefaultLanguage } = useSelector((state) => state.topBar);
  const [language, setLanguage] = useState('es');
  const { customAreaApi } = useApi();
  const [searchValue, setSearchValue] = useState('');
  const [pageSettings, setPageSettings] = useState({});
  const [pageSize, setPageSize] = useState(10);
  const grid = useRef(null);

  let data;
  let dropInstance;

  useEffect(() => {
    const lang = selectedDefaultLanguage.slice(0, 2);
    setLanguage(lang);
    setCulture(lang);
    L10n.load(L10nObj);
  }, [selectedDefaultLanguage]);

  const { handleSelectNotificationReview } = useNotification();

  const [, getCommunications] = customAreaApi(
    {
      url: `${window.API_V1}/gen/not/${cusNavId}/search/all?login=${mail}`,
      method: 'POST',
    },
    { manual: true }
  );

  const [, getMassiveCommunicationsCount] = customAreaApi(
    {
      url: `${window.API_V1}/gen/not/${cusNavId}/search/all/count?login=${mail}`,
      method: 'POST',
    },
    { manual: true }
  );

  const setLoader = (value) => {
    if (!document.querySelector('.e-spinner-pane')) return;
    if (value) {
      document.querySelector('.e-spinner-pane').style.display = 'block';
    } else {
      document.querySelector('.e-spinner-pane').style.display = 'none';
    }
  };

  const getData = async (state) => {
    setLoader(true);
    let filtering;
    let sortingFilter = sorting;

    if (state?.action?.action === 'filter' || state?.action?.action === 'clearFilter') {
      filtering = state?.where?.[0].predicates || [];
      setFilters(filtering);
    }
    const currentFilters = filtering || filters;
    const dataFilters = currentFilters.map((x) => {
      if (
        x.predicates &&
        x.predicates.length > 1 &&
        (x.predicates[0].field === 'created' || x.predicates[0].field === 'modified')
      ) {
        return {
          model: 'GeneralNotify',
          field: x.predicates[0].field.charAt(0).toUpperCase() + x.predicates[0].field.slice(1),
          filter: [x.predicates[0].value, x.predicates[1].value],
          isGreaterThan: true,
        };
      }

      let filter = {
        model: 'GeneralNotify',
        field: x.field.charAt(0).toUpperCase() + x.field.slice(1),
        filter: [x.value],
        IsRegexDiacritics: true,
      };
      if (x?.operator === 'greaterthan') {
        filter = { ...filter, isGreaterThan: true };
      }
      if (x?.operator === 'lessthan') {
        filter = { ...filter, isLessThan: true };
      }
      if (x?.operator === 'contains') {
        filter = { ...filter, IsRegexDiacritics: true };
      }
      return filter;
    });

    if (state?.action?.requestType === 'sorting' && state?.action?.columnName) {
      sortingFilter = {
        model: 'GeneralNotify',
        field: capitalizeFirstLetter(state?.action?.columnName),
        asc: state?.action?.direction === 'Ascending',
      };
    }

    if (state?.action?.requestType === 'sorting' && !state?.action?.columnName) {
      sortingFilter = null;
    }

    setSorting(sortingFilter);

    dataFilters.push(
      sortingFilter || {
        model: 'GeneralNotify',
        field: 'Created',
        asc: false,
      }
    );

    if (!isCustomer) {
      dataFilters.push({
        model: 'GeneralNotify',
        field: 'Name',
        filter: null,
        isRegex: false,
      });
      dataFilters.push({
        model: 'GeneralNotify',
        field: 'ClientId',
        filter: [clientId],
      });
    }

    const body = {
      filters: dataFilters,
      reduce: {
        limit: state.take,
        skip: state.skip / pageSize + 1,
      },
      ...((searchValue || generalSearchText) && {
        filterText: {
          regexFilter: searchValue || generalSearchText,
        },
      }),
    };

    try {
      const {
        data: { data: count },
      } = await getMassiveCommunicationsCount({
        data: body,
      });

      const {
        data: { data: communications },
      } = await getCommunications({
        data: body,
      });

      dispatch(setMassiveCommunicationsCount(count));
      setLoader(false);
      return {
        count: count || 0,
        result: communications || [],
      };
    } catch (error) {
      console.error(`Error Contacts: ${error}`);
      return {
        count: 0,
        result: [],
      };
    }
  };

  const dataStateChange = async (state) => {
    setPageSize(state.take);
    const requestType = state?.action?.requestType;
    if (requestType === 'stringfilterrequest') return;
    const dataSource = await getData(state);
    if (grid.current) {
      grid.current.dataSource = dataSource;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      const state = { skip: 0, take: pageSize };
      await dataStateChange(state);
    };
    setPageSettings({ pageCount: 3, pageSize, pageSizes: [5, 10, 20, 50, 75, 100] });
    fetchData();
  }, []);

  const getFilter = (field) => {
    const filter = filters.find((x) => x.field === field);
    return filter?.value || '';
  };

  const customFilter = {
    ui: {
      create: (args) => {
        document.getElementsByClassName('e-flm_optrdiv')[0].style.display = 'none';
        document.getElementsByClassName('e-flmenu-valuediv')[0].style.padding = '0';
        const flValInput = createElement('input', { className: 'flm-input' });
        args.target.appendChild(flValInput);
        dropInstance = new TextBox({
          type: 'text',
          placeholder: t('results_filter_search_value'),
          value: getFilter(args.column.field),
        });
        dropInstance.appendTo(flValInput);
      },
      read: (args) => {
        grid.current.removeFilteredColsByField(args.column.field);
        args.fltrObj.filterByColumn(args.column.field, 'contains', dropInstance.value);
      },
      write: (args) => {
        if (args.filteredValue) {
          dropInstance.value = args.filteredValue;
        }
      },
    },
  };

  useImperativeHandle(ref, () => ({
    async doSearch() {
      const state = { skip: 0, take: pageSize };
      await getCounters();
      await dataStateChange(state);
    },
  }));

  const handleSearch = (e) => {
    if (e) {
      const { value } = e.target;
      setSearchValue(value);
    } else {
      setSearchValue('');
    }
  };

  const handleRowSelected = (row) => {
    const { data: currentNotification } = row;
    if (breadcrumbs.length > 1) {
      breadcrumbs.forEach(() => {
        dispatch(removeBreadcrumb());
      });
    }
    dispatch(setShowTypeNotifications(true));
    handleSelectNotificationReview(currentNotification);
    const today = new Date();
    if (today >= new Date(currentNotification.date)) {
      history.push(`/notificacion_details`, { isClientNotify: false });
    } else if (!isCustomer) {
      history.push(`/notificacion_details`, { isClientNotify: false });
    } else {
      history.push(`/edit_notificacion`, { params: currentNotification });
    }
  };

  const onSearch = async () => {
    const state = { skip: 0, take: pageSize };
    await dataStateChange(state);
  };

  const onInputChange = async (e) => {
    if (e.key === 'Enter') {
      await onSearch();
    }
  };

  useEffect(() => {
    (async () => {
      if (searchValue === '') {
        const state = { skip: 0, take: pageSize };
        await dataStateChange(state);
      }
    })();
  }, [searchValue]);

  return (
    <DocumentationResultsWrapper>
      <div className="filter-container">
        <div className="form flex-column" noValidate>
          <div className="d-flex flex-row align-items-center h-50 ml-filter-section selector-filter-by-wrapper" />
          <TextField
            borderRadius={2}
            className="mb-4 m-0 h-50 filter-input"
            inputClassName="w-100"
            withButtonClear
            rightIcon={
              <SearchIcon fill={colors.black} onClick={onSearch} style={{ cursor: 'pointer' }} />
            }
            placeholder={t('results_search_placeholder_notification')}
            value={searchValue ?? ''}
            onChange={handleSearch}
            onKeyDown={onInputChange}
          />
        </div>
      </div>

      <div className="control-pane">
        <div className="control-section">
          <GridComponent
            id="Grid"
            dataSource={data}
            ref={(g) => {
              grid.current = g;
            }}
            allowPaging
            pageSettings={pageSettings}
            dataStateChange={dataStateChange}
            allowSorting
            allowFiltering
            filterSettings={{
              type: 'Menu',
              ignoreAccent: true,
              operators: {
                dateOperator: [
                  { value: 'equal', text: t('filters.equal') },
                  { value: 'greaterthan', text: t('filters.greaterthan') },
                  { value: 'lessthan', text: t('filters.lessthan') },
                ],
              },
            }}
            locale={language}
            rowSelected={(args) => {
              handleRowSelected(args);
            }}
          >
            <ColumnsDirective>
              <ColumnDirective
                field="name"
                headerText={t('notification_column_issue')}
                minWidth="200"
                width="auto"
                textAlign="Left"
                filter={customFilter}
              />
              <ColumnDirective
                field="created"
                headerText={t('panel_table_height')}
                width="180"
                textAlign="Left"
                type="date"
                format={{ type: 'date', format: 'dd/MM/yyyy HH:mm' }}
              />
              <ColumnDirective
                field="modified"
                headerText={t('panel_table_date')}
                width="180"
                textAlign="Left"
                type="date"
                format={{ type: 'date', format: 'dd/MM/yyyy HH:mm' }}
              />
            </ColumnsDirective>
            <Inject services={[Page, Filter, Resize]} />
          </GridComponent>
        </div>
        <div id="waitingpopup" className="waitingpopup">
          <span id="gif" className="image" />
        </div>
      </div>
    </DocumentationResultsWrapper>
  );
});

MassiveCommunicationResults.propTypes = {
  getCounters: PropTypes.func.isRequired,
};

export default MassiveCommunicationResults;
